import React, { useState, useEffect } from 'react';

const scoreColor = s => s >= 4?'#2E7D32':s >= 3?'#1565C0':s >= 2?'#F57F17':'#C62828';

// ── Stack detection ──────────────────────────────────────────────────────────
function getMode(eng) {
  if (!eng) return 'microsoft';
  const msIdentity = (eng.identityPlatforms||[]).includes('microsoft365');
  const msAI = (eng.aiWorkloads||[]).includes('azure_openai');
  if (msIdentity || msAI) return 'microsoft';
  return 'none';
}

// ── Microsoft scan feed ──────────────────────────────────────────────────────
const MS_FEED = [
  {type:'info',    text:'[Step 1/8] Authenticating to tenant via delegated OAuth token...'},
  {type:'info',    text:'[Step 2/8] Graph API — Purview DSPM AI app discovery... 12 apps detected, 0 registered in Purview AI Hub. AI.1.3 scored: 1/5'},
  {type:'info',    text:'[Step 3/8] Checking Purview DLP policies for Copilot location... 0 of 8 policies cover Copilot prompts. AI.3.3 scored: 1/5'},
  {type:'info',    text:'[Step 4/8] Querying Entra ID for agent identities... 0 registered agents, 4 orphaned service principals. AI.4.6 scored: 1/5'},
  {type:'warn',    text:'[Step 5/8] Defender for AI Services... Not enabled on 2 Azure OpenAI resources. AI.4.1 scored: 1/5 — ARM remediation template generated.'},
  {type:'info',    text:'[Step 6/8] Sentinel KQL — ATLAS detection rules... 0 ATLAS-mapped rules found across 47 total rules. AI.6.8 scored: 1/5'},
  {type:'info',    text:'[Step 7/8] Conditional Access policies... 11 policies retrieved. MFA coverage: 28% of users. AI.5.4 scored: 2/5'},
  {type:'success', text:'[Step 8/8] Writing 14 scored controls to SAARA control store... Complete. Layer 1 collection finished.'},
];

const MS_RESULT = { controls: 14, gaps: 11, avg: 1.4 };

// Scan results — scores written to API when scan completes
const SCAN_SCORES = {
  'AI.1.3': { score:1, status:'missing', notes:'0 of 12 detected AI apps registered in Purview. No formal AI system register exists.' },
  'AI.2.8': { score:1, status:'missing', notes:'No Entra Agent ID deployment. 0 agent identities registered. No agentic governance framework.' },
  'AI.3.3': { score:1, status:'missing', notes:'No Copilot DLP policy location configured. Prompt data unprotected. 34% of SharePoint content accessed by Copilot has no sensitivity label.' },
  'AI.3.6': { score:1, status:'missing', notes:'No training pipeline monitoring in Sentinel. No alerting rules for data anomalies.' },
  'AI.3.7': { score:2, status:'partial', notes:'SharePoint-grounded Copilot uses user-delegated permissions. No explicit scoping per application.' },
  'AI.4.1': { score:1, status:'missing', notes:'Defender for AI Services not enabled. Prompt injection attacks undetected at runtime.' },
  'AI.4.2': { score:1, status:'missing', notes:'No prompt firewall or guardrail solution deployed.' },
  'AI.4.6': { score:1, status:'missing', notes:'No Entra Agent ID deployment. 0 agent identities registered. No permissions inventory.' },
  'AI.4.8': { score:3, status:'partial', notes:'Azure OpenAI private endpoint confirmed. Content filtering enabled. Rate limiting not configured. Input validation partial.' },
  'AI.4.9': { score:2, status:'partial', notes:'Azure Monitor enabled for OpenAI. No Sentinel alerting rules for API anomalies.' },
  'AI.5.4': { score:1, status:'missing', notes:'Shadow AI detection not enabled in Entra Internet Access. 29 unsanctioned AI apps identified via browser signals.' },
  'AI.6.1': { score:2, status:'partial', notes:'Azure Monitor captures OpenAI API calls. No content-level monitoring or alerting for harmful outputs.' },
  'AI.6.7': { score:2, status:'partial', notes:'Generic incident management process applies. No AI-specific classification or escalation path.' },
  'AI.6.8': { score:1, status:'missing', notes:'0 MITRE ATLAS-mapped analytics rules in Sentinel workspace. AI-specific threat detection blind spot.' },
};

// Layer 1 control deep-dives (Microsoft API examples only)
const CONTROL_TABS = [
  { key:'ai13', label:'AI.1.3 — AI System Register' },
  { key:'ai41', label:'AI.4.1 — Prompt Injection Defence' },
  { key:'ai68', label:'AI.6.8 — ATLAS Detection Rules' },
];

const CONTROL_DETAIL = {
  ai13: {
    id:'AI.1.3', domain:'AI Governance & Policy · D1',
    steps:[
      {num:'1',color:'sn-blue',title:'Logic App trigger — HTTP from SAARA portal',desc:'SAARA portal calls the Logic App HTTP trigger with tenant ID and access token.',code:'POST https://prod-xx.eastasia.logic.azure.com/workflows/{id}/triggers/manual/run\n{\n  "tenant_id": "contoso.onmicrosoft.com",\n  "assessment_id": "SAARA-2026-001"\n}'},
      {num:'2',color:'sn-blue',title:'Graph API — Purview DSPM AI app discovery',desc:'Calls Purview DSPM to discover all AI apps in the tenant including Shadow AI.',code:'GET https://graph.microsoft.com/v1.0/security/aiInteractions/insights\nAuthorization: Bearer {token}\nScope: SecurityEvents.Read.All',result:[['AI apps detected','12'],['Apps in Purview AI Hub','0'],['Apps with business owner','0'],['Shadow AI apps','9']]},
      {num:'3',color:'sn-green',title:'Scoring rule — deterministic',scoring:[['#C62828','0 apps registered','Score 1','pill-red'],['#F57F17','Partial registration (<50%)','Score 2','pill-amber'],['#1565C0','All apps registered with owner','Score 3','pill-blue'],['#2E7D32','Register reviewed quarterly','Score 4+','pill-green']]},
      {num:'4',color:'sn-green',title:'Write to SAARA control store',code:'PUT https://saara-api/api/controls/AI.1.3\n{\n  "score": 1, "status": "missing",\n  "evidence_source": "Purview DSPM",\n  "auto_scored": true\n}'},
    ]
  },
  ai41: {
    id:'AI.4.1', domain:'Infrastructure & Model Security · D4',
    steps:[
      {num:'1',color:'sn-blue',title:'ARM API — Defender for AI Services',code:'GET https://management.azure.com/subscriptions/{id}/providers\n    /Microsoft.Security/pricings/AI?api-version=2024-01-01',result:[['Defender for AI Services','Not enabled'],['Prompt Shield status','Off'],['Azure OpenAI resources','2'],['With content filtering','1 of 2']]},
      {num:'2',color:'sn-green',title:'Scoring rule — deterministic',scoring:[['#C62828','Not enabled','Score 1','pill-red'],['#F57F17','Content filtering only','Score 2','pill-amber'],['#1565C0','Prompt Shield on all endpoints','Score 3','pill-blue'],['#2E7D32','Prompt Shield + custom rules + alerting','Score 4+','pill-green']]},
      {num:'3',color:'sn-amber',title:'Generate ARM remediation template',desc:'Logic App generates the ARM template to enable Defender for AI when gap confirmed.',code:'{\n  "type": "Microsoft.Security/pricings",\n  "name": "AI",\n  "properties": {\n    "pricingTier": "Standard",\n    "extensions": [{"name": "PromptShield", "isEnabled": "true"}]\n  }\n}'},
    ]
  },
  ai68: {
    id:'AI.6.8', domain:'Monitoring & Incident Response · D6',
    steps:[
      {num:'1',color:'sn-blue',title:'Log Analytics REST — KQL query against Sentinel',code:'POST https://api.loganalytics.io/v1/workspaces/{workspace-id}/query\n{\n  "query": "SecurityAlert\n  | where AlertType startswith \'ATLAS\'\n  | summarize RuleCount=count() by AlertType"\n}',result:[['ATLAS-mapped rules','0'],['Sentinel rules (total)','47'],['AI-specific rules','0'],['ATLAS tactics covered','0 of 16']]},
      {num:'2',color:'sn-green',title:'Scoring rule — deterministic',scoring:[['#C62828','0 ATLAS rules','Score 1','pill-red'],['#F57F17','1–3 tactics','Score 2','pill-amber'],['#1565C0','4–8 tactics + tested rules','Score 3','pill-blue'],['#2E7D32','All 16 ATLAS tactics covered','Score 4+','pill-green']]},
    ]
  },
};

// ── StepPanel component ──────────────────────────────────────────────────────
function StepPanel({ step }) {
  const numColors = {'sn-blue':'#E8F0FE|#1565C0','sn-green':'#E8F5E9|#2E7D32','sn-amber':'#FFF8E1|#F57F17','sn-purple':'#F3E5F5|#6A1B9A','sn-gray':'#F1F3F5|#455A64'};
  const [bg, fg] = (numColors[step.color]||'#F1F3F5|#455A64').split('|');
  return (
    <div style={{display:'flex',gap:12,marginBottom:14,alignItems:'flex-start'}}>
      <div style={{width:26,height:26,borderRadius:'50%',background:bg,color:fg,fontSize:11,fontWeight:500,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:2}}>{step.num}</div>
      <div style={{flex:1}}>
        <div style={{fontSize:12,fontWeight:500,color:'#0D1B2A',marginBottom:3}}>{step.title}</div>
        {step.desc && <div style={{fontSize:11,color:'#546E7A',marginBottom:6,lineHeight:1.5}}>{step.desc}</div>}
        {step.code && <div style={{background:'#0D1B2A',borderRadius:6,padding:'10px 12px',fontFamily:'Courier New,monospace',fontSize:10,color:'#85B7EB',lineHeight:1.6,overflowX:'auto',whiteSpace:'pre',marginBottom:6}}>{step.code}</div>}
        {step.result && (
          <div style={{background:'#F5F7F9',border:'0.5px solid #E0E4E8',borderRadius:6,padding:'8px 12px',marginTop:6}}>
            <div style={{fontSize:10,fontWeight:500,color:'#9E9E9E',marginBottom:6,textTransform:'uppercase',letterSpacing:'0.04em'}}>API response (sample)</div>
            {step.result.map(([k,v],i) => (
              <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:i<step.result.length-1?'0.5px solid #E0E4E8':'none',fontSize:11}}>
                <span style={{color:'#546E7A'}}>{k}</span>
                <span style={{fontWeight:500,color:v==='0'||v==='Not enabled'||v==='Off'||v.startsWith('0 of')||v==='None configured'?'#C62828':'#0D1B2A'}}>{v}</span>
              </div>
            ))}
          </div>
        )}
        {step.scoring && (
          <div style={{border:'0.5px solid #E0E4E8',borderRadius:6,padding:'8px 12px',marginTop:6,background:'#fff'}}>
            <div style={{fontSize:10,fontWeight:500,color:'#9E9E9E',marginBottom:6,textTransform:'uppercase',letterSpacing:'0.04em'}}>Scoring rules</div>
            {step.scoring.map(([col,label,score,pill],i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0',borderBottom:i<step.scoring.length-1?'0.5px solid #F0F2F5':'none',fontSize:11}}>
                <div style={{width:8,height:8,borderRadius:'50%',background:col,flexShrink:0}}/>
                <span style={{flex:1,color:'#455A64'}}>{label}</span>
                <span style={{fontSize:10,color:'#9E9E9E'}}>→</span>
                <span className={`pill ${pill}`}>{score}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── No automation screen ─────────────────────────────────────────────────────
function NoAutomationScreen({ engagement, setPage }) {
  const identityLabels = (engagement?.identityPlatforms||[]).map(id =>
    ({microsoft365:'Microsoft 365',google:'Google Workspace',other_identity:'Other/On-premise'}[id]||id)
  );
  const aiLabels = (engagement?.aiWorkloads||[]).map(id =>
    ({azure_openai:'Azure OpenAI',aws_bedrock:'AWS Bedrock',google_vertex:'Google Vertex AI',saas_ai:'Third-party SaaS AI',onprem_ai:'On-premise AI',none:'None yet'}[id]||id)
  );

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:20,padding:'10px 14px',background:'#fff',border:'1px solid #E8ECF0',borderRadius:8,flexWrap:'wrap'}}>
        <span style={{fontSize:11,color:'#7A8899'}}>Environment:</span>
        {identityLabels.map(l => <span key={l} className="pill pill-gray">{l}</span>)}
        {aiLabels.map(l => <span key={l} className="pill pill-gray">{l}</span>)}
        <span className="pill pill-gray" style={{marginLeft:'auto'}}>0 controls auto-scored</span>
      </div>

      <div style={{background:'#fff',border:'1px solid #E8ECF0',borderRadius:10,padding:24,marginBottom:20,textAlign:'center'}}>
        <div style={{fontSize:28,marginBottom:12,opacity:0.3}}>—</div>
        <div style={{fontSize:15,fontWeight:600,color:'#0D1B2A',marginBottom:8}}>
          Automated scoring is not available for this environment
        </div>
        <div style={{fontSize:12,color:'#546E7A',lineHeight:1.6,maxWidth:520,margin:'0 auto',marginBottom:16}}>
          The selected environment does not have Microsoft APIs that SAARA can call automatically.
          All 55 controls will be assessed through document review and stakeholder interviews.
          GCP and AWS connectors are on the Phase 2 roadmap.
        </div>
        <div style={{display:'inline-flex',gap:8}}>
          <span className="pill" style={{background:'#F3E5F5',color:'#6A1B9A',fontSize:11}}>Layer 2 document review: 47 controls</span>
          <span className="pill pill-gray" style={{fontSize:11}}>Layer 3 interviews: 8 controls</span>
        </div>
      </div>

      <div className="card" style={{marginTop:20}}>
        <div className="card-header">
          <div className="card-header-title">Why automated scoring is not available</div>
        </div>
        <div className="card-body">
          {[
            {platform:'Microsoft 365 / Azure',reason:'Not selected. Microsoft Graph API, Sentinel KQL, Defender for AI, Purview DSPM, and Entra ID power all 14 auto-scored controls. Requires delegated OAuth consent.'},
            {platform:'Google / GCP',reason:'Phase 2 roadmap. GCP Cloud Asset API, Security Command Center, and Vertex AI API connectors are planned.'},
            {platform:'AWS Bedrock',reason:'Phase 2 roadmap. AWS IAM Access Analyzer and Amazon Bedrock API connectors are planned.'},
          ].map((r,i) => (
            <div key={i} style={{display:'flex',gap:12,padding:'8px 0',borderBottom:i<2?'1px solid #F0F2F5':'none',fontSize:12,color:'#455A64',alignItems:'flex-start'}}>
              <span style={{color:'#B0BAC5',fontWeight:600,flexShrink:0}}>-</span>
              <div><span style={{fontWeight:500,color:'#0D1B2A'}}>{r.platform}:</span> {r.reason}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function AutoScored({ API, domains, engagement, setPage, refresh }) {
  const mode = getMode(engagement);
  const assessor = engagement?.assessor || 'Assessor';

  const [activeTab, setActiveTab] = useState(CONTROL_TABS[0]?.key);
  const [running, setRunning]     = useState(false);
  const [feed, setFeed]           = useState([]);
  const [scanDone, setScanDone]   = useState(false);
  const [controls, setControls]   = useState([]);
  const [domFilter, setDomFilter] = useState('all');
  const [showDeepDive, setShowDeepDive] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(null); // control id being overridden
  const [overrideScore, setOverrideScore] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');

  const loadControls = () => {
    fetch(`${API}/api/controls`)
      .then(r => r.json())
      .then(data => setControls(data.filter(c => c.auto_scored)));
  };

  useEffect(() => { loadControls(); }, [API]);

  const scoredControls = controls.filter(c => c.score != null);
  const confirmedCount = controls.filter(c => c.score_confirmed).length;
  const allConfirmed = confirmedCount === controls.length && controls.length > 0 && scoredControls.length === controls.length;
  const hasScores = scoredControls.length > 0;

  const confirmControl = async (controlId) => {
    await fetch(`${API}/api/controls/${controlId}/confirm?confirmed_by=${encodeURIComponent(assessor)}`, { method: 'POST' });
    loadControls();
    if (refresh) refresh();
  };

  const confirmAll = async () => {
    for (const c of controls.filter(x => !x.score_confirmed)) {
      await fetch(`${API}/api/controls/${c.id}/confirm?confirmed_by=${encodeURIComponent(assessor)}`, { method: 'POST' });
    }
    loadControls();
    if (refresh) refresh();
  };

  const submitOverride = async (controlId) => {
    if (overrideScore === null) return;
    await fetch(`${API}/api/controls/${controlId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        score: overrideScore,
        override_reason: overrideReason,
        score_source: 'assessor_override',
        score_confirmed: true,
        confirmed_by: assessor,
      }),
    });
    setOverrideOpen(null);
    setOverrideScore(null);
    setOverrideReason('');
    loadControls();
    if (refresh) refresh();
  };

  const runScan = () => {
    if (running) return;
    setRunning(true); setFeed([]); setScanDone(false);
    MS_FEED.forEach((item, i) => {
      setTimeout(() => {
        setFeed(prev => [...prev, {...item, id:i}]);
        if (i === MS_FEED.length-1) {
          // Write scan scores to the API
          setTimeout(async () => {
            for (const [controlId, result] of Object.entries(SCAN_SCORES)) {
              await fetch(`${API}/api/controls/${controlId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  score: result.score,
                  status: result.status,
                  assessor_notes: result.notes,
                  score_source: 'auto',
                }),
              });
            }
            loadControls();
            if (refresh) refresh();
            setRunning(false);
            setScanDone(true);
          }, 600);
        }
      }, i * 900);
    });
  };

  const feedBg  = t => t==='success'?'#E8F5E9':t==='warn'?'#FFF8E1':'#E8F0FE';
  const feedDot = t => t==='success'?'#2E7D32':t==='warn'?'#F57F17':'#1565C0';

  const filtered = domFilter==='all' ? controls : controls.filter(c => c.domain===domFilter);
  const ctrl = CONTROL_DETAIL[activeTab];

  if (mode === 'none') {
    return <NoAutomationScreen engagement={engagement} setPage={setPage}/>;
  }

  return (
    <div>

      {/* ── Progress summary ── */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:18}}>
        <div className="stat-card">
          <div className="stat-val" style={{color:'#1565C0',fontSize:28}}>{controls.length}</div>
          <div className="stat-lbl">Auto-scored controls</div>
          <div className="stat-sub">{hasScores ? `${scoredControls.length} scored via scan` : 'Pending — run scan to score'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-val" style={{color: !hasScores ? '#B0BAC5' : allConfirmed ? '#2E7D32' : '#F57F17',fontSize:28}}>
            {hasScores ? `${confirmedCount}/${controls.length}` : '—'}
          </div>
          <div className="stat-lbl">Assessor confirmed</div>
          <div className="stat-sub">{!hasScores ? 'Run scan first' : allConfirmed ? 'All confirmed — ready for next step' : `${controls.length - confirmedCount} need review`}</div>
        </div>
        <div className="stat-card">
          <div className="stat-val" style={{color:'#6A1B9A',fontSize:28}}>~33</div>
          <div className="stat-lbl">Document review</div>
          <div className="stat-sub">Layer 2 — Azure OpenAI</div>
        </div>
        <div className="stat-card">
          <div className="stat-val" style={{color:'#455A64',fontSize:28}}>8</div>
          <div className="stat-lbl">Assessor interviews</div>
          <div className="stat-sub">Layer 3 — Human judgment</div>
        </div>
      </div>

      {/* ── Review alert ── */}
      {hasScores && !allConfirmed && (
        <div className="info-box info-box-amber" style={{marginBottom:16,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <strong>{controls.length - confirmedCount} auto-scored control{controls.length - confirmedCount !== 1 ? 's' : ''} need assessor review.</strong> Confirm each score or override with justification before proceeding to document uploads.
          </div>
          <button onClick={confirmAll} style={{
            fontSize:11, padding:'6px 14px', background:'#2E7D32', color:'#fff',
            border:'none', borderRadius:6, cursor:'pointer', fontWeight:600, whiteSpace:'nowrap', marginLeft:12,
          }}>Confirm all scores</button>
        </div>
      )}

      {allConfirmed && (
        <div style={{
          background:'#E8F5E9', border:'1px solid #A5D6A7', borderRadius:10,
          padding:'14px 18px', marginBottom:16,
        }}>
          <div style={{fontSize:13,fontWeight:600,color:'#1B5E20'}}>All auto-scored controls confirmed</div>
          <div style={{fontSize:11,color:'#2E7D32',marginTop:2}}>Layer 1 complete — use the workflow bar above to continue to Document Uploads.</div>
        </div>
      )}

      {/* ── Scan section ── */}
      <div className="card" style={{marginBottom:18}}>
        <div className="card-header">
          <div>
            <div className="card-header-title">Layer 1 — Automated collection <span style={{fontSize:11,fontWeight:400,color:'#7A8899',marginLeft:6}}>via Microsoft Stack</span></div>
            <div className="card-header-sub">Logic App calls Graph API, Sentinel, Defender, Purview, Entra ID — scores {MS_RESULT.controls} controls deterministically</div>
          </div>
          <button className={running?'btn-secondary':'btn-primary'} disabled={running} onClick={runScan} style={{opacity:running?0.7:1}}>
            {running ? 'Scanning...' : scanDone ? 'Run again' : 'Run automated scan'}
          </button>
        </div>
        <div className="card-body">
          {feed.length > 0 && (
            <div style={{display:'flex',flexDirection:'column',gap:5,marginBottom:scanDone?14:0}}>
              {feed.map(item => (
                <div key={item.id} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'7px 12px',borderRadius:6,background:feedBg(item.type),fontSize:11,lineHeight:1.4}}>
                  <div style={{width:7,height:7,borderRadius:'50%',background:feedDot(item.type),flexShrink:0,marginTop:3}}/>
                  <span style={{color:'#0D1B2A'}}>{item.text}</span>
                </div>
              ))}
            </div>
          )}

          {scanDone && (
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
              <div style={{background:'#E8F0FE',borderRadius:8,padding:'12px 14px',textAlign:'center'}}>
                <div style={{fontSize:22,fontWeight:700,color:'#1565C0'}}>{MS_RESULT.controls}</div>
                <div style={{fontSize:10,color:'#7A8899'}}>Controls auto-scored</div>
              </div>
              <div style={{background:'#FFEBEE',borderRadius:8,padding:'12px 14px',textAlign:'center'}}>
                <div style={{fontSize:22,fontWeight:700,color:'#C62828'}}>{MS_RESULT.gaps}</div>
                <div style={{fontSize:10,color:'#7A8899'}}>Gaps identified</div>
              </div>
              <div style={{background:'#FFF8E1',borderRadius:8,padding:'12px 14px',textAlign:'center'}}>
                <div style={{fontSize:22,fontWeight:700,color:'#F57F17'}}>{MS_RESULT.avg}</div>
                <div style={{fontSize:10,color:'#7A8899'}}>Average maturity</div>
              </div>
            </div>
          )}

          {feed.length === 0 && !scanDone && (
            <div style={{textAlign:'center',padding:'20px 0',color:'#B0BAC5',fontSize:12}}>
              Click "Run automated scan" to start Layer 1 collection. The scan calls 8 Microsoft APIs and scores {MS_RESULT.controls} controls in ~60 seconds.
            </div>
          )}
        </div>
      </div>

      {/* ── Assessor review table ── */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
        <div className="section-title" style={{marginBottom:0,borderBottom:'none',paddingBottom:0}}>
          {hasScores ? `Assessor review — ${confirmedCount} of ${controls.length} confirmed` : `Auto-scored controls — ${controls.length} pending scan`}
        </div>
        <div style={{display:'flex',gap:6}}>
          {['all',...(domains||[]).map(d=>d.id)].map(d => (
            <div key={d} className={`domain-tab${domFilter===d?' active':''}`} style={{fontSize:10,padding:'3px 8px'}} onClick={() => setDomFilter(d)}>
              {d === 'all' ? 'All' : d}
            </div>
          ))}
        </div>
      </div>

      <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:18}}>
        {filtered.map(c => {
          const isConfirmed = c.score_confirmed;
          const isOverriding = overrideOpen === c.id;
          const isOverridden = c.score_source === 'assessor_override';

          return (
            <div key={c.id} style={{
              background:'#fff', border:'1px solid #E8ECF0', borderRadius:10, overflow:'hidden',
              borderLeft: c.score == null ? '3px solid #E0E4E8' : isConfirmed ? '3px solid #4CAF50' : '3px solid #FFA000',
            }}>
              {/* Main row */}
              <div style={{display:'grid', gridTemplateColumns:'70px 1fr 90px 50px auto', alignItems:'center', gap:12, padding:'10px 14px'}}>
                {/* Control ID */}
                <div>
                  <span style={{fontSize:11,fontWeight:600,color:'#1565C0'}}>{c.id}</span>
                  <div style={{fontSize:9,color:'#B0BAC5'}}>{c.domain}</div>
                </div>

                {/* Finding */}
                <div style={{minWidth:0}}>
                  <div style={{fontSize:11,color:'#455A64',lineHeight:1.3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {c.assessor_notes || c.statement}
                  </div>
                  <div style={{fontSize:10,color:'#B0BAC5',marginTop:2,display:'flex',gap:8,alignItems:'center'}}>
                    {c.source.includes('KQL')||c.source==='Sentinel'
                      ? <span className="chip chip-kql">{c.source}</span>
                      : <span className="chip chip-ms">{c.source}</span>}
                    <span>{c.category}</span>
                    {isOverridden && <span style={{fontSize:9,padding:'1px 6px',borderRadius:6,background:'#FFF3E0',color:'#E65100',fontWeight:500}}>Overridden from {c.original_auto_score}/5</span>}
                  </div>
                </div>

                {/* Status */}
                <div>
                  {c.status==='missing'&&<span className="pill pill-red">Gap</span>}
                  {c.status==='partial'&&<span className="pill pill-amber">Partial</span>}
                  {c.status==='provided'&&<span className="pill pill-green">Compliant</span>}
                  {c.status==='pending'&&<span className="pill pill-gray">Pending</span>}
                </div>

                {/* Score */}
                <div style={{fontWeight:700,fontSize:14,color:c.score!=null?scoreColor(c.score):'#B0BAC5',textAlign:'center'}}>
                  {c.score!=null?`${c.score}/5`:'—'}
                </div>

                {/* Actions */}
                <div style={{display:'flex',gap:6,alignItems:'center',justifyContent:'flex-end',minWidth:180}}>
                  {c.score == null ? (
                    <span style={{fontSize:10,color:'#B0BAC5'}}>Run scan to score</span>
                  ) : isConfirmed ? (
                    <span style={{fontSize:10,color:'#2E7D32',fontWeight:500,display:'flex',alignItems:'center',gap:4}}>
                      <span style={{fontSize:13}}>&#10003;</span> Confirmed by {c.confirmed_by || assessor}
                    </span>
                  ) : (
                    <>
                      <button onClick={() => confirmControl(c.id)} style={{
                        fontSize:10, padding:'5px 12px', background:'#E8F5E9', color:'#2E7D32',
                        border:'1px solid #A5D6A7', borderRadius:6, cursor:'pointer', fontWeight:600,
                      }}>Confirm</button>
                      <button onClick={() => { setOverrideOpen(isOverriding ? null : c.id); setOverrideScore(c.score); setOverrideReason(''); }} style={{
                        fontSize:10, padding:'5px 12px', background:'#FFF3E0', color:'#E65100',
                        border:'1px solid #FFE0B2', borderRadius:6, cursor:'pointer', fontWeight:500,
                      }}>{isOverriding ? 'Cancel' : 'Override'}</button>
                    </>
                  )}
                </div>
              </div>

              {/* Sub-controls */}
              {(c.sub_controls||[]).length > 0 && (
                <div style={{padding:'0 14px 8px 84px',background:'#FAFBFD',borderTop:'1px solid #F0F2F5'}}>
                  {c.sub_controls.map(sc => (
                    <div key={sc.id} style={{display:'flex',gap:10,alignItems:'flex-start',padding:'6px 0',borderBottom:'1px solid #F0F2F5'}}>
                      <span style={{fontSize:10,fontWeight:600,color:'#0078D4',minWidth:60}}>{sc.id}</span>
                      <div style={{fontSize:10,color:'#455A64',lineHeight:1.4}}>
                        {sc.statement}
                        <div style={{fontSize:9,color:'#7A8899',marginTop:2}}>Evidence: {sc.evidence_required}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Override panel */}
              {isOverriding && (
                <div style={{padding:'10px 14px 14px',background:'#FFFBF5',borderTop:'1px solid #FFE0B2',display:'flex',gap:12,alignItems:'flex-end'}}>
                  <div style={{flex:'0 0 80px'}}>
                    <div style={{fontSize:10,color:'#7A8899',marginBottom:4}}>New score</div>
                    <div style={{display:'flex',gap:4}}>
                      {[1,2,3,4,5].map(s => (
                        <div key={s} onClick={() => setOverrideScore(s)} style={{
                          width:28, height:28, borderRadius:6, fontSize:12, fontWeight:600,
                          display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer',
                          background: overrideScore===s ? scoreColor(s) : '#fff',
                          color: overrideScore===s ? '#fff' : scoreColor(s),
                          border: `2px solid ${overrideScore===s ? scoreColor(s) : '#E0E4E8'}`,
                        }}>{s}</div>
                      ))}
                    </div>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:10,color:'#7A8899',marginBottom:4}}>Override reason (required for audit trail)</div>
                    <input
                      type="text"
                      value={overrideReason}
                      onChange={e => setOverrideReason(e.target.value)}
                      placeholder="e.g. Evidence reviewed — partial implementation confirmed via walkthrough"
                      style={{width:'100%',fontSize:11,padding:'6px 10px',border:'1px solid #E0E4E8',borderRadius:6,background:'#fff'}}
                    />
                  </div>
                  <button onClick={() => submitOverride(c.id)} disabled={!overrideReason.trim()} style={{
                    fontSize:11, padding:'7px 16px', background: overrideReason.trim() ? '#E65100' : '#E0E4E8',
                    color:'#fff', border:'none', borderRadius:6, cursor: overrideReason.trim() ? 'pointer' : 'not-allowed',
                    fontWeight:600, whiteSpace:'nowrap',
                  }}>Save override</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── How it works (collapsible deep-dive) ── */}
      <button className="btn-ghost" onClick={() => setShowDeepDive(p => !p)} style={{fontSize:11,marginBottom:showDeepDive?12:0}}>
        {showDeepDive ? 'Hide API deep-dive' : 'How auto-scoring works — API deep-dive'}
      </button>

      {showDeepDive && (
        <>
          <div className="domain-tabs" style={{marginBottom:14}}>
            {CONTROL_TABS.map(t => (
              <div key={t.key} className={`domain-tab${activeTab===t.key?' active':''}`} onClick={() => setActiveTab(t.key)}>
                {t.label}
              </div>
            ))}
          </div>

          {ctrl && (
            <div className="card" style={{marginBottom:16}}>
              <div className="card-header">
                <div>
                  <div className="card-header-title">{ctrl.id} — Layer 1 automated scoring</div>
                  <div className="card-header-sub">{ctrl.domain}</div>
                </div>
                <span className="pill pill-green">Layer 1 — Automated</span>
              </div>
              <div className="card-body">
                {ctrl.steps.map((step, i) => <StepPanel key={i} step={step}/>)}
              </div>
            </div>
          )}
        </>
      )}

    </div>
  );
}
